import { ErrorBoundary } from '@sentry/react';
import { muiCache } from '@utils/ssr/emotionCache';
import {
  Alert,
  AlertTheme,
  Button,
  ButtonTheme,
  Dialog,
  ThemeProvider,
  Typography,
  TypographyType,
} from '@vivino/js-react-common-ui';
import { useI18N } from '@vivino/js-web-common';
import React, { useContext, useState } from 'react';
import ReactDOM from 'react-dom';
import { CouponContext, CouponProvider } from 'vivino-js/context/CouponContext/CouponContext';

import SnackBarProvider from '@components/SnackBarProvider/SnackBarProvider';
import { DiscountTagRed } from '@components/doodles/DiscountTag/DiscountTag';

import styles from './campaignStyles.module.scss';

const TRANSLATIONS = {
  applyCouponInCart: 'common.apply_coupon_in_cart',
  couponCopied: 'common.coupon_copied',
  copyTheCoupon: 'common.copy_the_coupon',
};

export const injectCampaignCardButtons = () => {
  const campaignCardActionButtons = globalThis?.document?.getElementsByClassName(
    'article-campaign-card-button'
  );
  const customView = globalThis?.document?.getElementsByClassName(
    'custom-view'
  )?.[0] as HTMLElement;
  const dataset = customView?.dataset;

  if (campaignCardActionButtons.length > 0 && dataset?.type === 'voucher_code') {
    for (const button of Array.from(campaignCardActionButtons)) {
      const App = () => {
        const { t } = useI18N();
        const [open, setOpen] = useState(false);
        const { activateCoupon, storedCouponItem } = useContext(CouponContext);
        const [activated, setActivated] = useState(
          dataset.voucher_code === storedCouponItem?.couponCode
        );

        const closestLink = button.closest('a');
        if (closestLink) {
          closestLink.addEventListener('click', (e) => {
            setOpen(true);
            e.preventDefault();
          });
        }

        const handleCtaClick = () => {
          activateCoupon({
            couponCode: dataset.voucher_code,
            bodySuccess: dataset.body_success,
            bodyCheckoutApply: dataset.body_checkout_apply,
            bodyReminder: dataset.body_reminder,

            checkoutCtaCopy: dataset.cta_copy,
            couponExpiryDate: dataset.coupon_expiry_date,
            campaignId: Number(dataset.campaign_id),
            campaignSlug: dataset.campaign_slug,
            campaignTitle: dataset.campaign_title,
            campaignType: dataset.campaign_type,
          });
          setActivated(true);

          setTimeout(() => {
            setOpen(false);
          }, 3000);
        };

        return (
          <ErrorBoundary>
            <Button
              className="widget-article-campaign-strip_shopNowButton"
              onClick={() => setOpen(true)}
            >
              {(button as HTMLElement).dataset?.buttonText}
            </Button>

            <Dialog
              dismissable
              dialogTitle={
                <div className={styles.header}>
                  <DiscountTagRed />
                </div>
              }
              open={open}
              onClose={() => setOpen(false)}
            >
              <div className={styles.container}>
                <Typography type={TypographyType.TitleExtraLarge}>{dataset?.body_apply}</Typography>
                <Typography type={TypographyType.BodyMedium}>
                  {t(TRANSLATIONS.applyCouponInCart, { code: dataset.voucher_code })}
                </Typography>
                <div className={styles.cta}>
                  {activated ? (
                    <Alert fullWidth className={styles.alert} subdued theme={AlertTheme.Positive}>
                      {t(TRANSLATIONS.couponCopied)}
                    </Alert>
                  ) : (
                    <Button fullWidth theme={ButtonTheme.Primary} onClick={handleCtaClick}>
                      {t(TRANSLATIONS.copyTheCoupon)}
                    </Button>
                  )}
                </div>
              </div>
            </Dialog>
          </ErrorBoundary>
        );
      };

      ReactDOM.render(
        <ThemeProvider cache={muiCache}>
          <SnackBarProvider>
            <CouponProvider>
              <App />
            </CouponProvider>
          </SnackBarProvider>
        </ThemeProvider>,
        button
      );
    }
  }
};
